import {
  canSpendProactive,
  type GuidanceItemState,
  type GuidanceProfileState,
} from '@bookora/shared';

import {
  CHIP_RETIRE_AFTER_MS,
  HELP_CONTEXT_GAP_MS,
  HELP_DECLINE_BACKOFF_MS,
  OFFER_MAX_IMPRESSIONS,
  OFFER_REOFFER_AFTER_MS,
} from './timing';
import type { TourDef } from './tours/types';

/**
 * Pure decisions: may the welcome show, may a tour be offered, what does a
 * tour's row say. Budget math is imported from `@bookora/shared`, the same
 * functions the server folds with, so the panel and the server cannot
 * disagree about a spent budget.
 */

/** The first-run items: `WELCOME/welcome` (NEWCOMER) and `WELCOME/announce-guidance` (VETERAN). */
export const WELCOME_KEY = 'welcome';
export const ANNOUNCEMENT_KEY = 'announce-guidance';
export const FIRST_RUN_VERSION = 1;

/**
 * `in-progress` = started and left part-way WITHOUT saying no (a route change,
 * an overlay, anchors gone): «ادامه» resumes at the step it stopped on.
 * `skipped` = started and then closed («×», «رد کردن راهنما»): a deliberate
 * answer, so the chip retires like a decline and the tour starts afresh from
 * the Help menu and the hub.
 */
export type TourStatus =
  'new' | 'in-progress' | 'skipped' | 'done' | 'updated' | 'declined' | 'retired';

function ms(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

export function tourStatus(
  item: GuidanceItemState | null | undefined,
  tour: TourDef,
  now: Date,
): TourStatus {
  if (!item) return 'new';
  switch (item.status) {
    case 'COMPLETED':
      return item.version < tour.version ? 'updated' : 'done';
    case 'STARTED':
      return 'in-progress';
    case 'DISMISSED':
      // Closed part-way through, or declined at the offer.
      return item.startedCount > 0 ? 'skipped' : 'declined';
    case 'OFFERED':
    case 'SNOOZED': {
      if (item.startedCount > 0) return 'in-progress';
      const first = ms(item.firstOfferedAt);
      const stale = first !== null && now.getTime() - first >= CHIP_RETIRE_AFTER_MS;
      return item.impressions >= OFFER_MAX_IMPRESSIONS || stale ? 'retired' : 'new';
    }
    default:
      return 'new';
  }
}

/**
 * The page chip retires after a completion, a decline, a tour closed part-way,
 * two ignored offers or 14 days after the first offer. The Help menu and the
 * hub keep the tour reachable for ever.
 */
export function chipVisible(
  item: GuidanceItemState | null | undefined,
  tour: TourDef,
  now: Date,
): boolean {
  const status = tourStatus(item, tour, now);
  return status !== 'done' && status !== 'declined' && status !== 'skipped' && status !== 'retired';
}

export interface ProactiveContext {
  now: Date;
  profile: GuidanceProfileState;
  impersonating: boolean;
  /** `localStorage.bookora_guidance_last_offer`. */
  localLastOffer: string | null;
  /** The per-tab, per-Tehran-day guard. */
  tabGuardAllows: boolean;
}

export interface AutoOfferContext extends ProactiveContext {
  item: GuidanceItemState | null | undefined;
  /** Usable anchors resolved for the tour right now. */
  usableSteps: number;
  /** A tip card or a tour offer already appeared in this page visit, or guidance is busy. */
  blocked: boolean;
}

function autoOffersPaused(profile: GuidanceProfileState, now: Date): boolean {
  const until = ms(profile.autoOfferPausedUntil);
  return until !== null && until > now.getTime();
}

export function canAutoOffer(tour: TourDef, ctx: AutoOfferContext): boolean {
  if (!tour.autoOffer) return false;
  if (ctx.usableSteps < tour.minSteps) return false;
  if (ctx.impersonating || ctx.blocked || !ctx.tabGuardAllows) return false;
  if (!ctx.profile.autoOfferTours || autoOffersPaused(ctx.profile, ctx.now)) return false;

  const item = ctx.item;
  if (item) {
    const updated = item.status === 'COMPLETED' && item.version < tour.version;
    if (!updated) {
      if (item.status !== 'OFFERED') return false;
      if (item.startedCount > 0) return false;
      if (item.impressions >= OFFER_MAX_IMPRESSIONS) return false;
      const last = ms(item.lastOfferedAt);
      if (last !== null && ctx.now.getTime() - last < OFFER_REOFFER_AFTER_MS) return false;
      const first = ms(item.firstOfferedAt);
      if (first !== null && ctx.now.getTime() - first >= CHIP_RETIRE_AFTER_MS) return false;
    }
  }
  return canSpendProactive(ctx.profile, ctx.now, { localLastAt: ctx.localLastOffer });
}

/** Where a resume starts: the step an interrupted run stopped on; `undefined` = from the top. */
export function resumeIndexOf(
  item: GuidanceItemState | null | undefined,
  tour: TourDef,
  now: Date,
): number | undefined {
  if (tourStatus(item, tour, now) !== 'in-progress') return undefined;
  return item?.lastStep ?? 0;
}

export interface HelpOfferContext extends Omit<ProactiveContext, 'tabGuardAllows'> {
  /** The billing read-only state (every write refused with 402): the banner is the prompt. */
  clinicReadOnly: boolean;
  /** Guidance is busy, a foreign overlay is up, or the tab is hidden. */
  blocked: boolean;
  /** This tab's help guard (independent of the first-visit offers' one). */
  tabHelpAllows: boolean;
  /** This context's local backoff. */
  backoff: { shownAt: string | null; declinedAt: string | null };
}

/**
 * May the panel offer help to someone who seems stuck (critic C6)? It
 * deliberately ignores the tour's own status — a finished tour is exactly
 * what a stuck person may need again — and answers to:
 *
 *  · never while impersonating, never while the clinic is billing read-only;
 *  · never while guidance is busy, a foreign overlay is up or the tab hidden;
 *  · the person's own switch («پیشنهاد راهنما وقتی به کارم بیاید») and a
 *    running decline pause;
 *  · the ONE budget (`canSpendProactive`, no exemption) — a help offer spends it;
 *  · one per context a day, none for 14 days after a «نه، ممنون» there;
 *  · one per tab a day, counted apart from the first-visit offers.
 */
export function canOfferHelp(ctx: HelpOfferContext): boolean {
  if (ctx.impersonating || ctx.clinicReadOnly || ctx.blocked || !ctx.tabHelpAllows) return false;
  if (!ctx.profile.autoOfferTours || autoOffersPaused(ctx.profile, ctx.now)) return false;
  const now = ctx.now.getTime();
  const shown = ms(ctx.backoff.shownAt);
  if (shown !== null && now - shown < HELP_CONTEXT_GAP_MS) return false;
  const declined = ms(ctx.backoff.declinedAt);
  if (declined !== null && now - declined < HELP_DECLINE_BACKOFF_MS) return false;
  return canSpendProactive(ctx.profile, ctx.now, { localLastAt: ctx.localLastOffer });
}

/** No COMPLETED or DISMISSED welcome, and no snooze still running. */
export function canShowWelcome(item: GuidanceItemState | null | undefined, now: Date): boolean {
  if (!item) return true;
  if (item.status === 'COMPLETED' || item.status === 'DISMISSED' || item.status === 'NOT_RELEVANT')
    return false;
  if (item.status === 'SNOOZED') {
    const until = ms(item.snoozedUntil);
    return until === null || until <= now.getTime();
  }
  return true;
}

export interface WelcomeContext extends ProactiveContext {
  welcome: GuidanceItemState | null | undefined;
  announcement: GuidanceItemState | null | undefined;
  /**
   * The welcome waits (not a snooze): on the setup wizard (`/setup`) and the
   * checklist summary (`/settings?tab=onboarding`), and — for a brand-new owner mid-setup — on
   * the page of any open setup step, where the modal would land on top of the
   * very thing «انجامش بده» just opened.
   */
  onSetupWizard: boolean;
}

/** What the first-run surface is for this person right now. */
export function firstRunSurface(ctx: WelcomeContext): 'welcome' | 'announcement' | null {
  if (ctx.impersonating || !ctx.tabGuardAllows || ctx.onSetupWizard) return null;
  // The welcome is exempt from the daily cap but still spends it, and still
  // keeps the 10-minute gap.
  if (
    !canSpendProactive(ctx.profile, ctx.now, {
      localLastAt: ctx.localLastOffer,
      exemptFromDailyCap: true,
    })
  ) {
    return null;
  }
  if (ctx.profile.cohort === 'NEWCOMER')
    return canShowWelcome(ctx.welcome, ctx.now) ? 'welcome' : null;
  return canShowAnnouncement(ctx.announcement) ? 'announcement' : null;
}

/**
 * The veteran's one-time callout: shown until acknowledged either way, and at
 * most twice when it is simply ignored (the same retirement as a tour offer).
 */
export function canShowAnnouncement(item: GuidanceItemState | null | undefined): boolean {
  if (!item) return true;
  return item.status === 'OFFERED' && item.impressions < OFFER_MAX_IMPRESSIONS;
}

/** Whether the first-run surface is settled for good (nothing will pop in this tab). */
export function firstRunSettled(
  profile: GuidanceProfileState,
  welcome: GuidanceItemState | null | undefined,
  announcement: GuidanceItemState | null | undefined,
  now: Date,
): boolean {
  return profile.cohort === 'NEWCOMER'
    ? !canShowWelcome(welcome, now)
    : !canShowAnnouncement(announcement);
}

export function isSetupWizard(pathname: string, search: string): boolean {
  if (pathname === '/setup' || pathname === '/settings/onboarding') return true;
  if (pathname !== '/settings') return false;
  return new URLSearchParams(search).get('tab') === 'onboarding';
}
